import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import StatusFilter from '../../components/StatusFilter';
import DateRangePicker from '../../components/reports/DateRangePicker';
import { ROUTES } from '../../consts/general';
import { INBOUND_SHIPMENTS_OVERVIEW } from '../../consts/reports';
import { CLOSED, CANCELLED, OPEN, enumStatuses } from '../../consts/status';
import { getStatusColor } from '../../helpers/statusColor';
import { useDebounce } from '../../helpers/useDebounce';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';

const STATUS_OPTIONS = [OPEN, CLOSED, CANCELLED];
const PAGE_SIZE = 50;

const toLocalYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultShippedDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 90);
  return { startDate: toLocalYmd(start), endDate: toLocalYmd(end) };
};

const DEFAULT_COLUMNS = [
  { key: 'po_number', label: 'PO #', type: 'string', sortable: true, sticky: true },
  { key: 'box_number', label: 'Box #', type: 'number', sortable: true },
  { key: 'vendor', label: 'Vendor', type: 'string', sortable: true },
  { key: 'po_date', label: 'PO Date', type: 'date', sortable: true },
  { key: 'status', label: 'Status', type: 'string', sortable: true },
  { key: 'product', label: 'Product', type: 'string', sortable: true },
  { key: 'sku', label: 'SKU', type: 'string', sortable: true },
  { key: 'shipping_method', label: 'Shipping Method', type: 'string', sortable: true },
  { key: 'tracking_number', label: 'Tracking #', type: 'string', sortable: true },
  { key: 'date_shipped', label: 'Date Shipped', type: 'date', sortable: true },
  { key: 'est_receive_date', label: 'Est. Receive Date', type: 'date', sortable: true },
  { key: 'received_date', label: 'Received Date', type: 'date', sortable: true },
  { key: 'shipment_state', label: 'Shipment State', type: 'string', sortable: true },
  { key: 'received_qty', label: 'Received Qty', type: 'number', sortable: true },
  { key: 'shipped_qty', label: 'Shipped Qty', type: 'number', sortable: true },
  { key: 'ordered_qty', label: 'Ordered Qty', type: 'number', sortable: true },
  { key: 'received_pct', label: 'Received %', type: 'number', sortable: true },
  { key: 'past_due_days', label: 'Days Past Due', type: 'number', sortable: true },
];

const InboundShipmentsOverviewReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const values = (searchParams.get('statuses') || '').split(',').filter((value) => STATUS_OPTIONS.includes(value));
    return values;
  });
  const defaultShippedDateRange = getDefaultShippedDateRange();
  const [shippedFrom, setShippedFrom] = useState(() => searchParams.get('shippedFrom') || defaultShippedDateRange.startDate);
  const [shippedTo, setShippedTo] = useState(() => searchParams.get('shippedTo') || defaultShippedDateRange.endDate);
  const [hasTracking, setHasTracking] = useState(() => (
    ['', 'true', 'false'].includes(searchParams.get('hasTracking') || '')
      ? searchParams.get('hasTracking') || ''
      : ''
  ));
  const [quickFilter, setQuickFilter] = useState(() => (
    searchParams.get('quickFilter') === 'shipped_not_received' ? 'shipped_not_received' : ''
  ));
  const [sortField, setSortField] = useState('po_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const currentSearch = next.get('search') || '';
    const currentStatuses = next.get('statuses') || '';
    const currentShippedFrom = next.get('shippedFrom') || '';
    const currentShippedTo = next.get('shippedTo') || '';
    const currentHasTracking = next.get('hasTracking') || '';
    const currentQuickFilter = next.get('quickFilter') || '';
    const nextStatuses = selectedStatuses.join(',');
    if (
      currentSearch === debouncedSearch
      && currentStatuses === nextStatuses
      && currentShippedFrom === shippedFrom
      && currentShippedTo === shippedTo
      && currentHasTracking === hasTracking
      && currentQuickFilter === quickFilter
    ) return;
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    if (nextStatuses) next.set('statuses', nextStatuses);
    else next.delete('statuses');
    if (shippedFrom) next.set('shippedFrom', shippedFrom);
    else next.delete('shippedFrom');
    if (shippedTo) next.set('shippedTo', shippedTo);
    else next.delete('shippedTo');
    if (hasTracking) next.set('hasTracking', hasTracking);
    else next.delete('hasTracking');
    if (quickFilter) next.set('quickFilter', quickFilter);
    else next.delete('quickFilter');
    setSearchParams(next, { replace: true });
  }, [
    debouncedSearch,
    hasTracking,
    quickFilter,
    searchParams,
    selectedStatuses,
    setSearchParams,
    shippedFrom,
    shippedTo,
  ]);

  useEffect(() => {
    let mounted = true;
    fetch(`/api/v1/reports/${INBOUND_SHIPMENTS_OVERVIEW}/metadata`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load report metadata');
        return response.json();
      })
      .then((result) => {
        if (mounted && result.columns?.length) {
          setColumns(result.columns.map((column, index) => (
            index === 0 ? { ...column, sticky: true, sortable: true } : { ...column, sortable: true }
          )));
        }
      })
      .catch((metadataError) => {
        if (mounted) setError(metadataError.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
        sortField,
        sortDirection,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (selectedStatuses.length > 0) params.set('statuses', selectedStatuses.join(','));
      if (shippedFrom) params.set('shippedFrom', shippedFrom);
      if (shippedTo) params.set('shippedTo', shippedTo);
      if (hasTracking) params.set('hasTracking', hasTracking);
      if (quickFilter) params.set('quickFilter', quickFilter);
      const response = await fetch(`/api/v1/reports/${INBOUND_SHIPMENTS_OVERVIEW}?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Failed to fetch report data');
      }
      const result = await response.json();
      if (!isLatest(requestId)) return;
      setData(result.data || []);
      setTotalPages(result.totalPages || 1);
      setTotalRecords(result.totalRecords || 0);
    } catch (requestError) {
      if (requestError?.name === 'AbortError') return;
      if (!isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [
    beginRequest,
    currentPage,
    debouncedSearch,
    hasTracking,
    isLatest,
    quickFilter,
    selectedStatuses,
    shippedFrom,
    shippedTo,
    sortDirection,
    sortField,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const renderCell = (row, column) => {
    if (column.key === 'status') {
      const statusCode = Object.keys(enumStatuses).find((key) => enumStatuses[key] === row.status) || row.status;
      return (
        <span className={`inline-flex rounded px-3 py-0.5 text-sm font-medium ${getStatusColor(statusCode)}`}>
          {row.status || ''}
        </span>
      );
    }
    if (column.key === 'shipment_state') {
      const stateClasses = {
        Received: 'bg-green-100 text-green-800',
        'In Transit': 'bg-blue-100 text-blue-800',
        'Not Shipped': 'bg-gray-100 text-gray-700',
        Stale: 'bg-amber-100 text-amber-800',
      };
      return (
        <span className={`inline-flex rounded px-3 py-0.5 text-sm font-medium ${stateClasses[row.shipment_state] || ''}`}>
          {row.shipment_state === 'Stale' ? 'Stale — needs reconciliation' : row.shipment_state || ''}
        </span>
      );
    }
    if (column.key === 'past_due_days') {
      return <span className={Number(row.past_due_days) > 0 ? 'text-red-600' : ''}>{row.past_due_days ?? 0}</span>;
    }
    if (column.key === 'received_qty') {
      return `${row.received_qty ?? 0} / ${row.shipped_qty ?? 0} (${row.received_pct ?? 0}%)`;
    }
    if (column.key === 'received_pct') return `${row.received_pct ?? 0}%`;
    return row[column.key] ?? '';
  };

  const renderRows = () => data.map((row, index) => (
    <tr key={row.box_id || `${row.po_id}-${row.box_number ?? 'placeholder'}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
      {columns.map((column) => (
        <td key={column.key} className={`px-6 py-4 whitespace-nowrap text-sm ${column.key === 'past_due_days' ? 'text-right' : 'text-gray-900'}`}>
          {renderCell(row, column)}
        </td>
      ))}
    </tr>
  ));

  const exportButton = (
    <CSVExportButton
      columns={columns}
      filename={INBOUND_SHIPMENTS_OVERVIEW}
      onExport={async () => {
        const params = new URLSearchParams({ sortField, sortDirection });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (selectedStatuses.length > 0) params.set('statuses', selectedStatuses.join(','));
        if (shippedFrom) params.set('shippedFrom', shippedFrom);
        if (shippedTo) params.set('shippedTo', shippedTo);
        if (hasTracking) params.set('hasTracking', hasTracking);
        if (quickFilter) params.set('quickFilter', quickFilter);
        const response = await fetch(`/api/v1/reports/${INBOUND_SHIPMENTS_OVERVIEW}/export?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || 'Export failed');
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${INBOUND_SHIPMENTS_OVERVIEW}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
      disabled={isLoading}
      variant="outline"
    />
  );

  const filters = (
    <div className="contents">
      <input
        type="text"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search inbound shipments"
        className="w-44 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      <StatusFilter
        options={STATUS_OPTIONS}
        selectedOptions={selectedStatuses}
        tableData={data}
        onChange={(value) => {
          setSelectedStatuses(value);
          setCurrentPage(1);
        }}
        placeholder="Status"
      />
      <div className="w-64">
        <DateRangePicker
          startDate={shippedFrom}
          endDate={shippedTo}
          onDateChange={({ startDate, endDate }) => {
            setShippedFrom(startDate);
            setShippedTo(endDate);
            setCurrentPage(1);
          }}
          label="Date Shipped"
        />
      </div>
      <select
        value={hasTracking}
        onChange={(event) => {
          setHasTracking(event.target.value);
          setCurrentPage(1);
        }}
        className="w-44 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        <option value="">Tracking: any</option>
        <option value="true">Has tracking #</option>
        <option value="false">No tracking #</option>
      </select>
      <button
        type="button"
        onClick={() => {
          setQuickFilter((value) => (value ? '' : 'shipped_not_received'));
          setCurrentPage(1);
        }}
        className={quickFilter
          ? 'rounded-md border border-indigo-600 bg-indigo-600 px-3 py-2 text-sm text-white'
          : 'rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50'}
      >
        Shipped, not received
      </button>
    </div>
  );

  const pagination = totalRecords > 0 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">
        Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalRecords)} of {totalRecords} boxes
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          disabled={currentPage === 1}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
        <button
          onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
          disabled={currentPage === totalPages}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  ) : null;

  return (
    <ReportTemplate
      reportId={INBOUND_SHIPMENTS_OVERVIEW}
      description="Inbound shipment boxes by purchase order with shipping method, tracking, ship/receive dates and received vs shipped quantities (mirrors the Shipping page)."
      breadcrumbs={[
        { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
        { label: 'Inbound Shipments' },
      ]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={isLoading}
      error={error}
      filters={filters}
      exportButton={exportButton}
      data={data}
      columns={columns}
      pagination={pagination}
      emptyMessage="No inbound shipments match the current filters."
      onSort={handleSort}
      sortField={sortField}
      sortDirection={sortDirection}
      enableSorting
    >
      {renderRows()}
    </ReportTemplate>
  );
};

export default InboundShipmentsOverviewReport;
